import { Button, TextField } from '@mui/material';
import { Add, Business } from '@mui/icons-material';
import { TEXTS } from '../../core/texts';
import { ChangeEvent, useCallback, useState } from 'react';
import { Modal } from '../common/Modal';
import { ValidationErrorState } from '../../types/validationErrorState';
import { IBaseClientProfile } from '../../interfaces/clientProfile';
import { ColumnBoxFlexFullWidth, FlexBoxFullWidth } from '../common/boxes';
import { ButtonWithLoader } from '../common/ButtonWithLoader';
import { Validator } from '../../core/Validator';
import NAMES = Validator.NAMES;
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClientService } from '../../services/client';
import { useNotification } from '../../+state/useNotification';
import { CLIENT_LIST_QUERY_KEY } from './Clients';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../interfaces/errorResponse';
import { parseAxiosError } from '../../services/axios';

const initialState: IBaseClientProfile = {
  legalName: '',
  contactName: '',
  phone: '',
  email: ''
};

const CreateClient = () => {
  const { openErrorToast, openSuccessToast } = useNotification();
  const queryClient = useQueryClient();
  const [openedModal, setOpenedModal] = useState(false);
  const [state, setState] = useState<IBaseClientProfile>(initialState);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorState>({});
  
  const toggleModal = useCallback(() => setOpenedModal(prev => {
    if(prev) {
      setValidationErrors({});
      setState(initialState);
    }
    return !prev;
  }), []);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setState(prev => ({ ...prev, [name]: value }));
  }, []);

  const mutation = useMutation({
    mutationFn: (client: IBaseClientProfile) => ClientService.createClient(client),
    onSuccess: () => {
      openSuccessToast(TEXTS.SUCCESS_SAVED);
      toggleModal();
      queryClient.invalidateQueries([CLIENT_LIST_QUERY_KEY]);
    },
    onError: (err: AxiosError<IErrorResponse>) => openErrorToast(parseAxiosError(err))
  });

  const handleSubmitBtn = useCallback(() => {
    const validErrors = Validator.validate(state, NAMES.CLIENT);
    if (validErrors) {
      return setValidationErrors(validErrors);
    }
    mutation.mutate(state);
  }, [mutation, state]);

  return (
    <>
      <Button startIcon={<Add/>} onClick={toggleModal} variant={'contained'}>{TEXTS.CREATE_NEW_CLIENT}</Button>
      {
        openedModal &&
        <Modal
          maxWidth={800} onClose={toggleModal}
          title={TEXTS.CREATE_NEW_CLIENT}
          headerIcon={<Business color={'primary'}/>}
          dialogActions={<FlexBoxFullWidth justifyContent={'space-between'} alignItems={'center'}>
            <Button variant={'outlined'} color={'error'} sx={{ m: 1 }} onClick={toggleModal}>
              {TEXTS.CANCEL}
            </Button>
            <ButtonWithLoader variant={'outlined'} sx={{ m: 1 }} loading={mutation.isLoading} onClick={handleSubmitBtn}>
              {TEXTS.SAVE}
            </ButtonWithLoader>
          </FlexBoxFullWidth>}
        >
          <ColumnBoxFlexFullWidth>
            <TextField name={'legalName'} label={TEXTS.LEGAL_NAME} value={state.legalName}
                       error={!!validationErrors.legalName} helperText={validationErrors.legalName}
                       onChange={handleInputChange} sx={{ mb: 2 }}
            />
            <TextField name={'contactName'} label={TEXTS.CONTACT_NAME} value={state.contactName}
                       error={!!validationErrors.contactName} helperText={validationErrors.contactName}
                       onChange={handleInputChange} sx={{ mb: 2 }}
            />
            <TextField name={'email'} label={TEXTS.EMAIL} value={state.email}
                       error={!!validationErrors.email} helperText={validationErrors.email}
                       onChange={handleInputChange} sx={{ mb: 2 }}
            />
            <TextField name={'phone'} label={TEXTS.PHONE} value={state.phone}
                       error={!!validationErrors.phone} helperText={validationErrors.phone}
                       onChange={handleInputChange} sx={{ mb: 2 }}
            />
          </ColumnBoxFlexFullWidth>
        </Modal>
      }
    </>
  );
};

export default CreateClient;